/**
 * Namespaced browser storage.
 *
 * WHY THE PREFIX MATTERS
 * ----------------------
 * `localStorage` is scoped to an ORIGIN, not to a path. This app is served from
 * a sub-path of a domain that also hosts the main site and other applications,
 * so every one of them reads and writes the *same* localStorage bucket.
 *
 * A bare key like `theme` would therefore be shared with — and silently
 * overwritten by — a neighbouring app. Every key this app owns is prefixed so
 * it cannot collide in either direction.
 *
 * (Cookies are different: they *are* path-scoped, which is why the session
 * cookie sets `path` to the mount prefix. See server/src/middleware/auth.ts.)
 *
 * KEEP IN SYNC: web/index.html contains an inline pre-paint script that reads
 * the theme key directly, before this module is loaded. Changing STORAGE_PREFIX
 * means changing the literal there too.
 */
export const STORAGE_PREFIX = 'shiporder:';

export const StorageKeys = {
  theme: `${STORAGE_PREFIX}theme`,
  refreshInterval: `${STORAGE_PREFIX}refresh-interval`,
} as const;

/** Reads are defensive: storage can throw in private mode or when disabled. */
export function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota exceeded or storage disabled — a lost preference is not fatal */
  }
}
